import { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { TIERS, TIER_BY_KEY, MODIFIERS, cellLabel, factionIcon } from "@/lib/game";
import { X, StickyNote } from "lucide-react";
import { useTeam, colorFor } from "@/lib/team";
import { useAuth } from "@/lib/auth";

/**
 * Reusable rating cell.
 * - cell: {tier, modifier, note, source?} | null
 * - onSet({tier, modifier, note}) / onClear()
 * - armyName: opponent army/faction name (icon + label shown in the popup)
 * - cellId: stable id for live presence highlighting
 * - dashedWhenDefault: show a dashed border when the value is an inherited default
 */
export default function RatingCell({ cell, onSet, onClear, armyName = "", cellId = "", dashedWhenDefault = false, testid = "rating-cell" }) {
  const [open, setOpen] = useState(false);
  const team = useTeam();
  const { user } = useAuth();
  const tier = cell?.tier ? TIER_BY_KEY[cell.tier] : null;
  const mod = cell?.modifier || "neutral";
  const note = cell?.note || "";
  const isDefault = cell?.source === "default" || cell?.source === "fallback";
  const icon = armyName ? factionIcon(armyName) : null;

  useEffect(() => {
    if (cellId && team?.sendFocus) team.sendFocus(open ? cellId : null);
  }, [open, cellId]); // eslint-disable-line

  const editors = (team?.presence || []).filter((u) => cellId && u.focus === cellId && u.id !== user?.id);
  const editColor = editors.length ? colorFor(editors[0].id) : null;

  const push = (patch) => onSet({ tier: cell?.tier || "little_win", modifier: mod, note, ...patch });

  // Notes use local state + a debounced push so typing stays smooth and the cursor doesn't
  // jump when the value round-trips through the live-synced team state.
  const [noteText, setNoteText] = useState(note);
  const noteRef = useRef(null);
  const noteTimer = useRef(null);
  useEffect(() => {
    if (document.activeElement !== noteRef.current) setNoteText(note);
  }, [note]);
  const onNoteChange = (v) => {
    setNoteText(v);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => push({ note: v }), 400);
  };
  const flushNote = () => {
    clearTimeout(noteTimer.current);
    if (noteText !== (cell?.note || "")) push({ note: noteText });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          className="relative w-full h-full min-h-[48px] rounded-md flex flex-col items-center justify-center font-mono font-bold text-sm transition-transform hover:scale-[1.05]"
          style={{
            background: tier ? tier.bg : "#0f1623",
            border: `${dashedWhenDefault && isDefault ? "1px dashed" : "1px solid"} ${tier ? tier.border : "#1e293b"}`,
            color: tier ? tier.text : "#475569",
            opacity: dashedWhenDefault && isDefault ? 0.78 : 1,
            outline: editColor ? `2px solid ${editColor}` : "none",
            outlineOffset: "-1px",
            boxShadow: editColor ? `0 0 8px ${editColor}99` : "none",
          }}
          title={note ? note : isDefault ? "Default (click to override)" : ""}
        >
          {tier ? cellLabel(cell) : "—"}
          {note && <StickyNote className="h-2.5 w-2.5 absolute top-0.5 right-0.5 text-amber-300" data-testid="note-indicator" />}
          {editColor && (
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-semibold px-1 rounded whitespace-nowrap z-10"
              style={{ background: editColor, color: "#0b1020" }} data-testid="cell-editor-badge">
              {editors[0].username}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 bg-[#141c2a] border-slate-700 p-3" align="center">
        {armyName && (
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-800">
            {icon && <img src={icon} alt="" className="h-5 w-5 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />}
            <span className="text-xs font-semibold text-slate-200 truncate">{armyName}</span>
          </div>
        )}
        <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Evaluation</p>
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {TIERS.map((t) => (
            <button
              key={t.key}
              onClick={() => push({ tier: t.key })}
              className="px-2 py-2 rounded text-xs font-bold border transition-transform hover:scale-105"
              style={{ background: cell?.tier === t.key && !isDefault ? t.bg : "#0f1623", borderColor: t.border, color: t.text }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Tiebreaker</p>
        <div className="flex gap-1.5 mb-3">
          {MODIFIERS.map((m) => (
            <button
              key={m.key}
              data-testid="modifier-toggle"
              onClick={() => push({ modifier: m.key })}
              className={`flex-1 py-1.5 rounded text-sm font-bold border transition-colors ${
                mod === m.key ? "border-current" : "border-slate-700"
              }`}
              style={{ color: m.color, background: mod === m.key ? "rgba(255,255,255,0.06)" : "transparent" }}
              title={m.hint}
            >
              {m.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Note</p>
        <Textarea
          ref={noteRef}
          data-testid="rating-note-input"
          value={noteText}
          onChange={(e) => onNoteChange(e.target.value)}
          onBlur={flushNote}
          placeholder="Conditions / reminders (shown in the matrix)…"
          className="bg-slate-950/60 border-slate-800 text-[11px] h-16 mb-2"
        />
        <button onClick={() => { onClear(); setOpen(false); }}
          className="w-full text-xs text-slate-500 hover:text-red-400 flex items-center justify-center gap-1 py-1">
          <X className="h-3 w-3" /> Reset to default
        </button>
      </PopoverContent>
    </Popover>
  );
}
